import jp from 'jsonpath'
import { MeetingTemplatePreset } from '../../../../store/ai/meetingTemplate'
import { MEETING_TEMPLATE_COLUMNS } from '../../../../views/Setup/MeetingTemplates/utils'
import { SegmentMetadata } from '@zocks-communications/client-sdk-js'
import { AiResult } from '../../../../models/AiResult'
import { InputParametersHierarchyNodeByPresets } from '../../../../models/InputParametersHierarchy'

export const parseMetadata = (metadata?: string) => {
  if (!metadata) {
    return {}
  }
  try {
    return JSON.parse(metadata)
  } catch (e) {
    console.debug(`Could not parse metadata: ${metadata}`)
  }
  return {}
}

export const getOrderedMeetingTemplatePresets = (meetingTemplatePresets: MeetingTemplatePreset[]) => {
  return meetingTemplatePresets
    .map((meetingTemplatePreset, index) => {
      return {
        preset: meetingTemplatePreset,
        index,
      }
    })
    .sort((a, b) => {
      const { preset: aPreset, index: aIndex } = a
      const { preset: bPreset, index: bIndex } = b
      const aMetadata = parseMetadata(aPreset.metadata)
      const bMetadata = parseMetadata(bPreset.metadata)
      const aRow = aMetadata.row ?? aIndex
      const bRow = bMetadata.row ?? bIndex
      const aColumn = MEETING_TEMPLATE_COLUMNS.indexOf(aMetadata.column ?? 'A')
      const bColumn = MEETING_TEMPLATE_COLUMNS.indexOf(bMetadata.column ?? 'A')

      return aRow - bRow || aColumn - bColumn
    })
    .map(({ preset }) => preset)
}

export const getPresetResultsByInputParameterHierarchy = (
  aiFeaturePresetId: string,
  aiFeatureId: string,
  allAiResults: AiResult[],
  inputParametersHierarchyByPresets: InputParametersHierarchyNodeByPresets,
) => {
  const inputParameterHierarchyNodeForPreset = inputParametersHierarchyByPresets[aiFeaturePresetId]
  const isGlobalOrUser = !inputParameterHierarchyNodeForPreset || ['global', 'user'].includes(inputParameterHierarchyNodeForPreset.hierarchyId.level)
  const inputParameterHierarchyIdForPreset = inputParameterHierarchyNodeForPreset?.id ?? null
  return allAiResults
    .filter((r) => r.aiFeatureId === aiFeatureId)
    .filter((r) => r.inputParametersHierarchyId === inputParameterHierarchyIdForPreset || (isGlobalOrUser && !r.inputParametersHierarchyId))
    .sort((r1, r2) => r2.createdAt.localeCompare(r1.createdAt))
}

export const getSafeValueOnPath = (object: any, path: string, defaultValue: any = null) => {
  try {
    return jp.value(object, path)
  } catch (e) {
    console.error(e)
  }
  return defaultValue
}

export const isFirefox = () => {
  try {
    return navigator.userAgent.toLowerCase().includes('firefox')
  } catch (e) {
    console.error(e)
  }
  return false
}

const getElapsedTimeFromSessionStart = (time: string, roomSessionStartedAt?: string) => {
  try {
    const startedAt = new Date(roomSessionStartedAt ?? '').getTime()
    return new Date(parseFloat(time) * 1000 - startedAt).toISOString().slice(11, 19)
  } catch (e) {
    return ''
  }
}

export const getResultSegmentHeader = (metadata: SegmentMetadata, roomSessionStartedAt?: string) => {
  const start = getElapsedTimeFromSessionStart(metadata.startTime.toString(), roomSessionStartedAt)
  const end = getElapsedTimeFromSessionStart(metadata.endTime.toString(), roomSessionStartedAt)
  const topic = metadata.topic ? ` - Topic: ${metadata.topic}` : ''
  return `[${start}-${end}]${topic}`
}
